import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ArrowSquareOut, ArrowsClockwise, PencilSimple, Trophy, CaretDown, Check, X, Star, ChartLineUp } from "@phosphor-icons/react";

const ResultBadge = ({ value, size = "sm" }) => {
  const cls = value === "W"
    ? "bg-[#16ec06]/15 text-[#16ec06]"
    : value === "L"
      ? "bg-[#ff0026]/15 text-[#ff0026]"
      : "bg-zinc-700 text-zinc-300";
  const px = size === "lg" ? "px-2.5 py-1 text-sm" : "px-1.5 py-0.5 text-[10px]";
  return (
    <span className={`${px} rounded font-bold uppercase tracking-wider ${cls}`}>
      {value || "?"}
    </span>
  );
};

// Tournament result badge — "1" = winner, "F" = finalist, "3/4" = SF, "5/8" = QF, etc.
const ResultPill = ({ result }) => {
  if (!result) return <span className="text-zinc-500">—</span>;
  const r = String(result).trim();
  let label = r;
  let cls = "bg-zinc-700 text-zinc-200";
  if (r === "1") { label = "🏆 Winner"; cls = "bg-[#dfff00]/20 text-[#dfff00] border border-[#dfff00]/30"; }
  else if (r === "2" || r.toUpperCase() === "F") { label = "Finalist"; cls = "bg-[#dfff00]/10 text-[#dfff00]"; }
  else if (r === "3/4") { label = "SF"; cls = "bg-[#16ec06]/15 text-[#16ec06]"; }
  else if (r === "5/8") { label = "QF"; cls = "bg-[#16ec06]/10 text-[#16ec06]/90"; }
  else if (/^9\/16$/.test(r)) { label = "R16"; cls = "bg-zinc-600 text-zinc-200"; }
  else if (/^17\/32$/.test(r)) { label = "R32"; cls = "bg-zinc-700 text-zinc-300"; }
  return <span className={`px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap ${cls}`}>{label}</span>;
};

const SectionTournamentTable = ({ section, rows }) => {
  if (!rows?.length) return null;
  return (
    <div className="bg-[#09090b] border border-[#27272a] rounded-md overflow-hidden" data-testid={`lta-ranking-section-${section.replace(/\s+/g, "-")}`}>
      <div className="px-3 py-2 bg-[#0d0d0d] border-b border-[#27272a] flex items-center justify-between">
        <div className="font-heading uppercase tracking-tight text-sm">{section}</div>
        <div className="font-label">
          {rows.length} {rows.length === 1 ? "tournament" : "tournaments"} ·{" "}
          {rows.reduce((s, r) => s + (r.points || 0), 0).toLocaleString()} pts
        </div>
      </div>
      <div className="overflow-x-auto overflow-y-auto max-h-60 lta-scroll" data-testid={`lta-ranking-scroll-${section.replace(/\s+/g, "-")}`}>
        <table className="w-full text-xs">
          <thead className="text-zinc-500 sticky top-0 bg-[#0d0d0d] z-10">
            <tr>
              <th className="text-left font-label py-2 px-3">Week</th>
              <th className="text-left font-label py-2 px-3">Tournament</th>
              <th className="text-left font-label py-2 px-3">Event</th>
              <th className="text-left font-label py-2 px-3">Result</th>
              <th className="text-right font-label py-2 px-3">Points</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-[#27272a]" data-testid={`lta-ranking-row-${i}`}>
                <td className="py-2 px-3 text-zinc-400 font-mono whitespace-nowrap">{row.week || "—"}</td>
                <td className="py-2 px-3 text-zinc-200">
                  <div className="line-clamp-2 max-w-[24rem]">{row.tournament || "—"}</div>
                </td>
                <td className="py-2 px-3 text-zinc-400 whitespace-nowrap">{row.event || "—"}</td>
                <td className="py-2 px-3"><ResultPill result={row.result} /></td>
                <td className="py-2 px-3 text-white font-mono font-bold text-right whitespace-nowrap">{row.points != null ? row.points.toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function LtaResults({ playerId, lta_id, canEdit, onLtaIdChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftId, setDraftId] = useState(lta_id || "");
  const [openTour, setOpenTour] = useState(null);
  const [showAllTournaments, setShowAllTournaments] = useState(false);
  const [showMatchHistory, setShowMatchHistory] = useState(false);
  const [err, setErr] = useState("");

  const fetchResults = async (force = false) => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get(`/players/${playerId}/lta-results`, { params: force ? { force: true } : {} });
      setData(res.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDraftId(lta_id || "");
    if (lta_id) fetchResults(false);
    else setData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, lta_id]);

  const saveLtaId = async () => {
    const v = (draftId || "").trim();
    if (v && !/^\d+$/.test(v)) {
      setErr("LTA number must be digits only.");
      return;
    }
    try {
      const res = await api.patch(`/players/${playerId}`, { lta_id: v || null });
      onLtaIdChange?.(res.data.lta_id || "");
      setEditing(false);
      setErr("");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    }
  };

  // No LTA ID set
  if (!lta_id && !editing) {
    return (
      <section className="surface p-5" data-testid="lta-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-label">LTA Ranking</div>
            <h2 className="font-heading uppercase text-xl tracking-tight">No LTA number on file</h2>
            <p className="text-sm text-zinc-400 mt-1">Add the player's LTA membership number to pull their ranking and counting tournaments automatically.</p>
          </div>
          {canEdit && (
            <button onClick={() => setEditing(true)} className="btn-primary" data-testid="add-lta-btn">
              <PencilSimple size={14} weight="bold" /> Add LTA number
            </button>
          )}
        </div>
      </section>
    );
  }

  const stats = data?.statistics || {};
  const rankings = data?.rankings || {};
  const summary = Array.isArray(rankings.summary) ? rankings.summary : [];
  const combined = rankings.combined || null;
  const recentForm = rankings.recent_form || null;
  const recentMatches = Array.isArray(data?.recent_matches) ? data.recent_matches : [];

  // Combined summary excludes "Open Male" duplicate to highlight the player's actual age cats
  const combinedSummary = summary.filter(r => r._group === "combined");
  const recentFormSummary = summary.filter(r => r._group === "recent_form");

  // Determine the player's "primary" age category — smallest "NNU" rank value
  const primary = combinedSummary
    .filter(r => /\d+U/.test(r.category || "") && typeof r.rank === "number")
    .sort((a, b) => (a.rank || 1e9) - (b.rank || 1e9))[0]
    || combinedSummary[0];

  const hasCareer = typeof stats.career_wins === "number" || typeof stats.career_losses === "number";

  return (
    <section className="surface" data-testid="lta-section">
      {/* Header */}
      <div className="p-5 flex items-start justify-between gap-3 flex-wrap border-b border-[#27272a]">
        <div className="min-w-0">
          <div className="font-label">LTA Ranking</div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  className="input"
                  style={{ height: "2.25rem", width: "10rem" }}
                  value={draftId}
                  onChange={(e) => setDraftId(e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="e.g. 114284081"
                  data-testid="lta-id-input"
                  autoFocus
                />
                <button onClick={saveLtaId} className="btn-primary" style={{ height: "2.25rem", padding: "0 0.75rem" }} data-testid="lta-id-save"><Check size={14} weight="bold" /></button>
                <button onClick={() => { setEditing(false); setDraftId(lta_id || ""); setErr(""); }} className="btn-ghost"><X size={14} weight="bold" /></button>
              </div>
            ) : (
              <>
                <span className="text-sm text-zinc-300">LTA #</span>
                <span className="font-mono text-sm text-white">{lta_id}</span>
                {canEdit && (
                  <button onClick={() => setEditing(true)} className="btn-ghost" data-testid="edit-lta-btn"><PencilSimple size={12} weight="bold" /></button>
                )}
              </>
            )}
            {data?.player_name && !editing && (
              <span className="text-xs text-zinc-500">→ {data.player_name}</span>
            )}
          </div>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            <button onClick={() => fetchResults(true)} disabled={loading} className="btn-ghost text-xs" data-testid="lta-refresh">
              <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
              {loading ? "Syncing…" : "Refresh"}
            </button>
            {data?.profile_url && (
              <a href={data.profile_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs" data-testid="lta-deep-link">
                <ArrowSquareOut size={14} weight="bold" /> View on LTA
              </a>
            )}
          </div>
        )}
      </div>

      {err && <div className="m-4 text-sm text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-3">{err}</div>}

      {/* Content */}
      <div className="p-4 space-y-5">
        {loading && !data && <div className="skeleton h-24" />}

        {data?.error && (
          <div className="text-sm text-zinc-400">
            Couldn't fetch LTA data: {data.error}. Try again later or check the LTA number.
          </div>
        )}

        {/* Hero: Primary age-category headline */}
        {data && primary && (
          <div className="bg-gradient-to-br from-[#0a0a0a] to-[#121212] border border-[#27272a] rounded-md p-4" data-testid="lta-primary-rank">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-label flex items-center gap-1.5"><Star size={12} weight="fill" className="text-[#dfff00]" /> Primary age category</div>
                <div className="font-heading uppercase tracking-tight text-2xl mt-1">{primary.category}</div>
              </div>
              <div className="flex items-baseline gap-5 flex-wrap">
                <div className="text-right">
                  <div className="font-label">Rank</div>
                  <div className="text-3xl font-heading text-[#dfff00] tracking-tight">#{primary.rank?.toLocaleString() ?? "—"}</div>
                </div>
                <div className="text-right">
                  <div className="font-label">Total points</div>
                  <div className="text-3xl font-heading text-white tracking-tight">{primary.total_points?.toLocaleString() ?? "—"}</div>
                </div>
                <div className="text-right">
                  <div className="font-label">Tournaments</div>
                  <div className="text-3xl font-heading text-white tracking-tight">{primary.tournaments_count ?? "—"}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Form (separate younger-category placement) */}
        {recentForm && Object.keys(recentForm.tournaments_by_section || {}).length > 0 && (
          <div data-testid="lta-recent-form-block">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading uppercase tracking-tight text-lg flex items-center gap-2">
                <ChartLineUp size={18} weight="duotone" className="text-[#dfff00]" /> Recent Form
              </h3>
              {recentFormSummary[0]?.recent_form != null && (
                <div className="font-label">{recentFormSummary[0].category} · {recentFormSummary[0].recent_form?.toLocaleString()} pts · {recentFormSummary[0].tournaments_count} events</div>
              )}
            </div>
            <div className="space-y-3">
              {Object.entries(recentForm.tournaments_by_section).map(([section, rows]) => (
                <SectionTournamentTable key={section} section={section} rows={rows} />
              ))}
            </div>
          </div>
        )}

        {/* Combined Rankings — counting tournaments by category */}
        {combined && Object.keys(combined.tournaments_by_section || {}).length > 0 && (
          <div data-testid="lta-combined-block">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading uppercase tracking-tight text-lg flex items-center gap-2">
                <Trophy size={18} weight="duotone" className="text-[#dfff00]" /> Counting Tournaments
              </h3>
              <span className="font-label">{combined.title || "LTA Combined Rankings"}</span>
            </div>
            <div className="space-y-3">
              {Object.entries(combined.tournaments_by_section).map(([section, rows]) => (
                <SectionTournamentTable key={section} section={section} rows={rows} />
              ))}
            </div>
          </div>
        )}

        {/* Combined ranking summary grid (per age category) */}
        {combinedSummary.length > 0 && (
          <details className="bg-[#09090b] border border-[#27272a] rounded-md" data-testid="lta-ranking-by-category">
            <summary className="px-3 py-2 cursor-pointer font-label hover:bg-[#1a1a1a]">
              Ranking by age category ({combinedSummary.length})
            </summary>
            <div className="overflow-x-auto border-t border-[#27272a]">
              <table className="w-full text-xs">
                <thead className="text-zinc-500 bg-[#0d0d0d]">
                  <tr>
                    <th className="text-left font-label py-2 px-3">Category</th>
                    <th className="text-right font-label py-2 px-3">Rank</th>
                    <th className="text-right font-label py-2 px-3">Singles</th>
                    <th className="text-right font-label py-2 px-3">Doubles</th>
                    <th className="text-right font-label py-2 px-3">Total</th>
                    <th className="text-right font-label py-2 px-3">Tournaments</th>
                  </tr>
                </thead>
                <tbody>
                  {combinedSummary.map((r, i) => (
                    <tr key={i} className="border-t border-[#27272a]">
                      <td className="py-2 px-3 text-zinc-200">{r.category}</td>
                      <td className="py-2 px-3 text-right text-[#dfff00] font-mono font-bold">#{r.rank?.toLocaleString() ?? "—"}</td>
                      <td className="py-2 px-3 text-right text-zinc-300 font-mono">{r.singles_points ?? "—"}</td>
                      <td className="py-2 px-3 text-right text-zinc-300 font-mono">{r.doubles_points ?? "—"}</td>
                      <td className="py-2 px-3 text-right text-white font-mono font-bold">{r.total_points?.toLocaleString() ?? "—"}</td>
                      <td className="py-2 px-3 text-right text-zinc-300 font-mono">{r.tournaments_count ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}

        {/* Career stats footer (smaller, supportive) */}
        {hasCareer && (
          <div className="bg-[#09090b] border border-[#27272a] rounded-md p-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid="lta-career-stats">
            <div>
              <span className="font-label mr-2">Career W/L</span>
              <span className="text-[#16ec06] font-mono">{stats.career_wins ?? 0}</span>
              <span className="text-zinc-600 mx-1">/</span>
              <span className="text-[#ff0026] font-mono">{stats.career_losses ?? 0}</span>
              {stats.career_total > 0 && (
                <span className="text-zinc-500 ml-2 text-xs">({Math.round((stats.career_wins / stats.career_total) * 100)}% win rate, {stats.career_total} matches)</span>
              )}
            </div>
            {typeof stats.year_wins === "number" && (
              <div>
                <span className="font-label mr-2">This year</span>
                <span className="text-[#16ec06] font-mono">{stats.year_wins}</span>
                <span className="text-zinc-600 mx-1">/</span>
                <span className="text-[#ff0026] font-mono">{stats.year_losses ?? 0}</span>
                {stats.year_total > 0 && (
                  <span className="text-zinc-500 ml-2 text-xs">({stats.year_total} matches)</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Match history drill-down (optional) */}
        {recentMatches.length > 0 && (
          <details className="bg-[#09090b] border border-[#27272a] rounded-md" data-testid="lta-match-history">
            <summary className="px-3 py-2 cursor-pointer font-label hover:bg-[#1a1a1a]">
              Match history ({recentMatches.length})
            </summary>
            <div className="overflow-x-auto border-t border-[#27272a]">
              <table className="w-full text-xs">
                <thead className="text-zinc-500 bg-[#0d0d0d]">
                  <tr>
                    <th className="text-left font-label py-2 px-3">Date</th>
                    <th className="text-left font-label py-2 px-3">Tournament</th>
                    <th className="text-left font-label py-2 px-3">Round</th>
                    <th className="text-left font-label py-2 px-3">Opponent</th>
                    <th className="text-left font-label py-2 px-3">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMatches.slice(0, 12).map((m, i) => (
                    <tr key={i} className="border-t border-[#27272a]">
                      <td className="py-2 px-3 text-zinc-400 whitespace-nowrap">{m.date || "—"}</td>
                      <td className="py-2 px-3 text-zinc-200">
                        <div className="line-clamp-1 max-w-[18rem]">{m.tournament || "—"}</div>
                      </td>
                      <td className="py-2 px-3 text-zinc-400 whitespace-nowrap">{m.round || "—"}</td>
                      <td className="py-2 px-3 text-zinc-200">{m.opponent || "—"}</td>
                      <td className="py-2 px-3"><ResultBadge value={m.result} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}

        {/* Empty state */}
        {data && !data.error && !primary && !combined && !recentForm && (
          <div className="text-sm text-zinc-500 text-center py-4">
            No ranking data found yet on LTA for this player.
          </div>
        )}

        {data && (
          <div className="text-xs text-zinc-600 border-t border-[#27272a] pt-3">
            {data.cached
              ? <>Data cached — <button className="underline" onClick={() => fetchResults(true)} data-testid="lta-resync">re-sync from LTA now</button></>
              : <>Live from LTA at {data.fetched_at ? new Date(data.fetched_at * 1000).toLocaleString() : "now"}.</>
            }
          </div>
        )}
      </div>
    </section>
  );
}
